import { delay } from "lodash"
import React, { useEffect, useState } from "react"
import { Badge, NavDropdown, Tab, Tabs, Toast, ToastContainer } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import ScaleLoader from "react-spinners/ScaleLoader"
import Flag from "react-world-flags"
import api from "../../api"
import { useAppDispatch, useAppSelector } from "../../app/hooks"
import CheckCircleIcon from "../../assets/checkCircleIcon.svg"
import AE from "../../assets/country/AE.svg"
import IQ from "../../assets/country/IQ.svg"
import SA from "../../assets/country/SA.svg"
import ErrorIcon from "../../assets/ErrorIcon.svg"
import { GLOBAL_CONSTANTS } from "../../constants/constants"
import { AppDispatch, RootState } from "../../store"
import { loadCountry } from "../../store/customer.store"
import {
    loadBags,
    loadBagsTransit,
    loadCountries,
    loadDestinations,
    loadMAWBAttachmentTypes,
    loadMAWBs,
    loadPallets,
    loadParcels,
    loadStatuses,
    resetSuccess,
    setSelectedCountry,
    setTab
} from "../../store/linehaul.store"
import Header from "../Layout/Header"
import BagModal from "./BagModal"
import LineHaulTable from "./LineHaulTable"
import MAWBModel from "./MAWBModel"
import MAWBPalletDetailsModal from "./MAWBPalletDetailsModal"
import PalletModal from "./PalletModal"
import UpdateMAWBStatusModel from "./UpdateMAWBStatusModel"
import UploadModal from "./UploadModal"

const LineHaul = () => {
    const dispatch = useAppDispatch() as AppDispatch
    const { t } = useTranslation()
    const { userInfo } = useAppSelector((state: RootState) => state.auth)
    const { bags, pallets, pallet, mawbs, mawb, loading, error, country, countriesWithCounts, success, tab } =
        useAppSelector((state: RootState) => state.linehaul)
    const { selectedWarehouse } = useAppSelector((state: RootState) => state.warehouses)
    const [selectedRows, setSelectedRows] = useState([] as number[])
    const [printLoading, setPrintLoading] = useState(false)
    const [errors, setErrors] = useState({} as Record<string, any>)
    const [toast, setToast] = useState({} as Record<string, any>)
    const [show, setShow] = useState("")
    const [options, setOptions] = useState({
        custom: true,
        totalSize: {
            pallets: 0,
            bags: 0,
            mawbs: 0,
            parcels: 0
        },
        page: {
            pallets: 1,
            bags: 1,
            mawbs: 1,
            parcels: 1
        },
        sizePerPage: GLOBAL_CONSTANTS.PAGE_SIZE,
        hideSizePerPage: true,
        hidePageListOnlyOnePage: true,
        showTotal: false,
        prePageText: t("Previous"),
        nextPageText: t("Next"),
        keyword: {
            pallets: "",
            bags: "",
            mawbs: "",
            parcels: ""
        },
        status: {
            mawbs: ""
        },
        from: {
            pallets: "",
            mawbs: ""
        },
        to: {
            pallets: "",
            mawbs: ""
        }
    })
    const _options = { ...options }
    options.totalSize.pallets = pallets.total ?? 0
    options.totalSize.mawbs = mawbs.total ?? 0
    const { countryList } = useAppSelector((state: RootState) => state.customers)
    const [data, setData] = useState({} as Record<string, any>)
    const countryChange = (countryCode: string) => {
        dispatch(setSelectedCountry(countryCode))
    }
    const [displayedPallet, setDisplayedPallet] = useState<any>()
    const [deleteDocument, setDeleteDocument] = useState<any>()
    const [mawbAttachmentUpload, setMawbAttachmentUpload] = useState<any>()

    useEffect(() => {
        if (countryList?.length === 0) {
            dispatch(loadCountry())
        }
    }, [countryList?.length])

    const defaultWarehouse = userInfo?.ParcelHubUserWarehouse?.find(
        (uw: Record<string, any>) => uw?.is_default
    )?.warehouse_id

    const showMessage = async (msg: string, severity: string, status: string) => {
        window.scrollTo({
            top: 0,
            left: 0,
            behavior: "smooth"
        })
        setToast({ msg, severity, status })
        await delay(() => setToast({}), 5000)
        setData({})
        setShow("")
        dispatch(resetSuccess(success ? undefined : true))
    }

    useEffect(() => {
        dispatch(loadCountries({ warehouse: selectedWarehouse?.toString() || defaultWarehouse?.toString() }))
    }, [])

    useEffect(() => {
        dispatch(loadDestinations())
    }, [])

    useEffect(() => {
        if (selectedWarehouse && selectedWarehouse > 0) {
            const afterSubmitReload = async () => {
                dispatch(
                    loadPallets({
                        warehouse: selectedWarehouse?.toString() || defaultWarehouse?.toString(),
                        destination: country,
                        page: options.page.pallets,
                        size: options?.sizePerPage,
                        keyword: options.keyword.pallets,
                        from: options.from.pallets,
                        to: options.to.pallets
                    })
                )
                dispatch(
                    loadBags({
                        warehouse: selectedWarehouse?.toString() || defaultWarehouse?.toString(),
                        destination: country,
                        page: options.page.bags,
                        size: options?.sizePerPage,
                        keyword: options.keyword.bags
                    })
                )
                dispatch(
                    loadBagsTransit({
                        warehouse: selectedWarehouse?.toString() || defaultWarehouse?.toString(),
                        destination: country
                    })
                )
                dispatch(
                    loadMAWBs({
                        warehouse: selectedWarehouse?.toString() || defaultWarehouse?.toString(),
                        destination: country,
                        page: options.page.mawbs,
                        size: options?.sizePerPage,
                        keyword: options.keyword.mawbs,
                        from: options.from.mawbs,
                        to: options.to.mawbs,
                        status: options.status.mawbs
                    })
                )
                dispatch(
                    loadParcels({
                        warehouse: selectedWarehouse?.toString() || defaultWarehouse?.toString(),
                        destination: country
                    })
                )
                dispatch(
                    loadParcels({
                        warehouse: selectedWarehouse?.toString() || defaultWarehouse?.toString(),
                        destination: country
                    })
                )
                dispatch(loadMAWBAttachmentTypes())
                // const labelLink = document.createElement("a") as HTMLAnchorElement
                // labelLink.className = "d-none"
                // labelLink.href = label || ""
                // labelLink.target = "_blank"
                // labelLink.click()
                // dispatch(resetSuccess(undefined))
            }
            afterSubmitReload()
        }
    }, [country, success, selectedWarehouse, options.sizePerPage, options.page])

    const returnCountryFlag = (country: string): string => {
        switch (country) {
            case "SA":
                return SA
            case "AE":
                return AE
            case "IQ":
                return IQ
            default:
                return ""
        }
    }

    const submitUpdateCreateMAWB = async (mawbData: Record<string, any>) => {
        try {
            let res = {} as Record<string, any>
            if (mawbData?.id) {
                res = await api.linehaul.updateMAWB({
                    mawbData: { ...mawbData, warehouse_id: selectedWarehouse }
                })
            } else {
                res = await api.linehaul.createMAWB({
                    mawbData: { ...mawbData, warehouse_id: selectedWarehouse }
                })
            }
            if (res?.data?.status) {
                showMessage(res?.data?.message ?? `Changes are saved`, "success", "Success")
                // setData({})
                // setSelectedRows([])
                // setShow("")
                // dispatch(resetSuccess(true))
            } else {
                throw new Error(res?.data?.message)
            }
        } catch (error: any) {
            let err = error?.message ?? error
            console.log(err)
            showMessage(`Changes are not saved, reason: ${err}`, "danger", "Error")
            // setData({})
            // setSelectedRows([])
            // setShow("")
            // dispatch(resetSuccess(undefined))
        }
    }

    const updateMAWBStatus = async (mawbStatusData: Record<string, any>) => {
        try {
            let res = await api.linehaul.updateMAWBStatus({
                mawbStatusData: { ...mawbStatusData, warehouse_id: selectedWarehouse }
            })
            if (res?.data?.status) {
                showMessage(res?.data?.message ?? `Changes are saved`, "success", "Success")
                // setShow("")
                // dispatch(resetSuccess(true))
            } else {
                throw new Error(res?.data?.message)
            }
        } catch (error: any) {
            let err = error?.message ?? error
            console.log(err)
            showMessage(`Changes are not saved, reason: ${err}`, "danger", "Error")
            // setData({})
            // setShow("")
            // dispatch(resetSuccess(undefined))
        }
    }

    const handleShowMAWBPalletDetails = (pallet: any) => {
        setDisplayedPallet(pallet)
        setShow("mawbPalletDetails")
    }

    const handleShowDocumentDeleteModal = (document: any) => {
        setDeleteDocument(document)
        setShow("documentDelete")
    }

    useEffect(() => {
        dispatch(loadStatuses({ warehouse: selectedWarehouse?.toString() || defaultWarehouse?.toString() }))
    }, [selectedWarehouse])

    return (
        <>
            <div className="economyconsol-container">
                {toast?.msg && (
                    <ToastContainer className="p-3" position={"top-end"}>
                        <Toast onClose={() => setToast({})} className="rounded">
                            <Toast.Header
                                closeButton
                                className={`${toast?.severity === "success" ? "bg-light-green" : "bg-danger"} border-bottom-0`}>
                                <div className="mr-auto d-flex align-items-center">
                                    <img src={toast?.severity === "success" ? CheckCircleIcon : ErrorIcon} />
                                    <span
                                        className={`text-${toast?.severity === "success" ? "black" : "white"} mx-1`}>
                                        <strong className="d-inline mx-2">{toast.status}</strong>
                                        <span>{t(toast.msg)}</span>
                                    </span>
                                </div>
                            </Toast.Header>
                        </Toast>
                    </ToastContainer>
                )}
                <Header
                    title={"Linehaul"}
                    subtitle={"Shopiniworld"}
                    data={pallets}
                    name="linehaul"
                    buttonId=".linehaulExportButton"
                    disableExport
                    renderExtraControls={
                        <div className="d-flex flex-row flex-nowrap justify-content-end align-items-center me-5">
                            <div className="d-flex align-items-center flex-nowrap">
                                <span className="me-2">From</span>
                                <NavDropdown
                                    title={
                                        <>
                                            <Flag
                                                className="d-inline-block me-3"
                                                style={{
                                                    width: 28,
                                                    height: 18
                                                }}
                                                code="us"
                                            />
                                            <span className="text-black">USA</span>
                                        </>
                                    }
                                    className="username-color border-2 rounded me-3">
                                    <NavDropdown.Item>USA</NavDropdown.Item>
                                </NavDropdown>
                            </div>
                            <div className="d-flex align-items-center flex-nowrap">
                                <span className="me-2">To</span>
                                <NavDropdown
                                    title={
                                        <>
                                            <Flag
                                                className="d-inline-block me-3"
                                                style={{
                                                    width: 28,
                                                    height: 18
                                                }}
                                                code={country}
                                            />
                                            <span className="text-black">
                                                {(countryList ?? []).find(
                                                    (c) =>
                                                        (c.iso2_code ?? "").toLowerCase() ===
                                                        (country ?? "").toLowerCase()
                                                )?.name ?? "N/A"}
                                            </span>
                                        </>
                                    }
                                    className="username-color border-2 rounded">
                                    {countriesWithCounts?.map((c: Record<string, any>) => (
                                        <NavDropdown.Item
                                            key={c.country_code}
                                            onClick={async () => {
                                                countryChange(c?.country_code)
                                            }}>
                                            {c.country}
                                        </NavDropdown.Item>
                                    ))}
                                </NavDropdown>
                            </div>
                        </div>
                    }></Header>

                {loading || !userInfo?.accessToken ? (
                    <div className="loader row justify-content-center">
                        <ScaleLoader color="#15397F" width={4} height={40} />
                    </div>
                ) : (
                    <div className="row d-flex tabs-data px-3">
                        <Tabs
                            id="pallets-tab"
                            className="mt-4"
                            activeKey={tab}
                            onSelect={(k: any) => {
                                dispatch(setTab(k as string))
                            }}>
                            <Tab
                                eventKey="bags"
                                title={
                                    <>
                                        {t("Bags")}
                                        <Badge className="mx-2 px-2">{bags?.bags?.length || 0}</Badge>
                                    </>
                                }>
                                <LineHaulTable
                                    data={bags?.bags || []}
                                    setSelectedRows={setSelectedRows}
                                    selectedRows={selectedRows}
                                    setShow={setShow}
                                    showMessage={showMessage}
                                    tableName="Bags"
                                    options={_options}
                                    setOptions={setOptions}
                                    recordDetails={data}
                                    setRecordDetails={setData}
                                />
                            </Tab>
                            <Tab
                                eventKey="pallets"
                                title={
                                    <>
                                        {t("Pallets")}
                                        <Badge className="mx-2 px-2">{pallets?.pallets?.length || 0}</Badge>
                                    </>
                                }>
                                <LineHaulTable
                                    data={pallets?.pallets || []}
                                    setSelectedRows={setSelectedRows}
                                    selectedRows={selectedRows}
                                    setShow={setShow}
                                    showMessage={showMessage}
                                    tableName={"Pallets"}
                                    options={_options}
                                    setOptions={setOptions}
                                    recordDetails={data}
                                    setRecordDetails={setData}
                                />
                            </Tab>
                            <Tab
                                eventKey="mawbs"
                                title={
                                    <>
                                        {t("MAWBS")}
                                        <Badge className="mx-2 px-2">{mawbs?.list_mawbs?.length || 0}</Badge>
                                    </>
                                }>
                                <LineHaulTable
                                    data={mawbs?.list_mawbs || []}
                                    setSelectedRows={setSelectedRows}
                                    selectedRows={selectedRows}
                                    setShow={setShow}
                                    tableName={"MAWBs"}
                                    options={_options}
                                    setOptions={setOptions}
                                    recordDetails={data}
                                    setRecordDetails={setData}
                                    showMessage={showMessage}
                                    showPalletDetails={handleShowMAWBPalletDetails}
                                    deleteDocument={handleShowDocumentDeleteModal}
                                    setMawbAttachmentUpload={setMawbAttachmentUpload}
                                />
                            </Tab>
                        </Tabs>
                    </div>
                )}
            </div>
            <PalletModal
                key={`${show}-Pallet`}
                show={show}
                setShow={setShow}
                warehouse={selectedWarehouse || defaultWarehouse}
                errors={errors}
                setErrors={setErrors}
                setSelectedRows={setSelectedRows}
                selectedRows={selectedRows}
                options={_options}
                setOptions={setOptions}
                showMessage={showMessage}
                // reloadAfterSubmit={dispatch(resetSuccess(true))}
            />
            <BagModal
                key={`${show}-Bag`}
                show={show}
                setShow={setShow}
                warehouse={selectedWarehouse || defaultWarehouse}
                errors={errors}
                setErrors={setErrors}
                setSelectedRows={setSelectedRows}
                selectedRows={selectedRows}
                options={_options}
                setOptions={setOptions}
                showMessage={showMessage}
            />
            <MAWBModel
                key={`${show}-MAWB`}
                show={show}
                setShow={setShow}
                warehouse={selectedWarehouse || defaultWarehouse}
                setSelectedRows={setSelectedRows}
                selectedRows={selectedRows}
                options={_options}
                setOptions={setOptions}
                data={data}
                setData={setData}
                submitUpdateCreateMAWB={submitUpdateCreateMAWB}
                showMessage={showMessage}
            />
            <UpdateMAWBStatusModel
                show={show}
                setShow={setShow}
                data={data}
                setData={setData}
                updateMAWBStatus={updateMAWBStatus}
            />
            <MAWBPalletDetailsModal
                show={show}
                setShow={setShow}
                data={displayedPallet}
                showMessage={showMessage}
            />
            <UploadModal
                key={`${show}-upload`}
                show={show}
                setShow={setShow}
                showMessage={showMessage}
                attachment={deleteDocument}
                mawb={mawbAttachmentUpload}
            />
        </>
    )
}

export default LineHaul
